import React, { useEffect, useState, useCallback } from 'react';

const C = {
  bg:'#070D0A', surface:'#0E1812', card:'#121E16', border:'#1C2E22',
  text:'#EDE8DC', muted:'rgba(237,232,220,0.55)', dim:'rgba(237,232,220,0.35)',
  gold:'#C9A84C', goldLt:'#E2C97E', red:'#ef4444', amber:'#f59e0b', green:'#22c55e',
};

const authHdr = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('erue_auth_token') || ''}`
});

interface MetaStatus { is_meta_verified: boolean; page_id: string|null; last_post: string|null; }
interface Asset { title: string; text: string; imageUrl?: string; }
interface Props { committeeId: string; festivalAssets?: Asset[]; }

export default function MetaDistributionPanel({ committeeId, festivalAssets = [] }: Props) {
  const [status,   setStatus]   = useState<MetaStatus|null>(null);
  const [loading,  setLoading]  = useState(true);
  const [message,  setMessage]  = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [posting,  setPosting]  = useState(false);
  const [feedback, setFeedback] = useState('');
  const [copied,   setCopied]   = useState<string|null>(null);
  const [socialLinks, setSocialLinks] = useState<any>({});
  const [configMode, setConfigMode] = useState(false);
  const [cfgForm, setCfgForm] = useState({ is_meta_verified: false, page_id:'' });

  const loadStatus = useCallback(async () => {
    try {
      setLoading(true);
      const r = await fetch('/api/integrations/meta/status', { headers: authHdr() });
      const d = await r.json();
      if (d.success) { setStatus(d); setCfgForm({ is_meta_verified:d.is_meta_verified, page_id:d.page_id||'' }); }
    } catch { setFeedback('Failed to load Meta status.'); }
    finally { setLoading(false); }
  }, []);

  const loadSocialLinks = useCallback(async () => {
    try {
      const r = await fetch(`/api/collab/social/PLATFORM/global`, { headers: authHdr() });
      const d = await r.json();
      if (d.success) setSocialLinks({ ...(d.globalLinks||[]).reduce((a:any,l:any)=>({...a,[l.platform]:l.url}),{}) });
    } catch {}
  }, []);

  useEffect(() => { loadStatus(); loadSocialLinks(); }, []);

  const copyToClipboard = async (text: string, key: string) => {
    try { await navigator.clipboard.writeText(text); setCopied(key); setTimeout(()=>setCopied(null),2000); }
    catch { setFeedback('Clipboard access denied.'); }
  };

  const handlePost = async () => {
    if (!message.trim()) return setFeedback('Message text required.');
    setPosting(true); setFeedback('');
    try {
      const r = await fetch('/api/integrations/meta/post', {
        method:'POST', headers:authHdr(),
        body:JSON.stringify({ message, imageUrl, committeeId })
      });
      const d = await r.json();
      if (d.success && d.method === 'api') { setFeedback('✅ Posted via Graph API.'); setMessage(''); setImageUrl(''); }
      else if (d.method === 'skipped')      setFeedback('⚠️ Meta not verified — use manual workflow below.');
      else                                  setFeedback(`❌ ${d.reason||'Unknown error'}`);
    } catch(e:any) { setFeedback(`❌ ${e.message}`); }
    finally { setPosting(false); }
  };

  const saveConfig = async () => {
    try {
      const r = await fetch('/api/admin/integrations/meta/config', {
        method:'PUT', headers:authHdr(), body:JSON.stringify(cfgForm)
      });
      const d = await r.json();
      if (d.success) { setFeedback('✅ Config saved.'); await loadStatus(); setConfigMode(false); }
      else setFeedback(`❌ ${d.error}`);
    } catch(e:any) { setFeedback(`❌ ${e.message}`); }
  };

  if (loading) return <div style={{ color:C.muted, padding:32, textAlign:'center' }}>Loading Meta integration status…</div>;

  return (
    <div style={{ fontFamily:'system-ui,sans-serif', color:C.text, display:'flex', flexDirection:'column', gap:16, padding:24, background:C.bg, minHeight:'100vh' }}>

      {/* Header */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:8 }}>
        <div>
          <div style={{ color:C.gold, fontWeight:700, fontSize:18 }}>📣 Meta Distribution Panel</div>
          <div style={{ color:C.muted, fontSize:12, marginTop:2 }}>Facebook & Instagram publishing — ERUE Festival</div>
        </div>
        <div style={{ display:'flex', gap:8, alignItems:'center' }}>
          <div style={{ padding:'4px 12px', borderRadius:20, fontSize:12, fontWeight:700, background:status?.is_meta_verified?'rgba(34,197,94,0.15)':'rgba(245,158,11,0.15)', color:status?.is_meta_verified?C.green:C.amber, border:`1px solid ${status?.is_meta_verified?C.green:C.amber}` }}>
            {status?.is_meta_verified?'✅ Verified':'⚠️ Unverified'}
          </div>
          <button onClick={() => setConfigMode(p=>!p)} style={{ padding:'5px 12px', borderRadius:6, border:`1px solid ${C.border}`, background:'transparent', color:C.muted, cursor:'pointer', fontSize:12 }}>⚙️ Config</button>
        </div>
      </div>

      {feedback && (
        <div style={{ padding:'10px 14px', borderRadius:6, fontSize:13, background:feedback.startsWith('✅')?'rgba(34,197,94,0.1)':'rgba(239,68,68,0.1)', color:feedback.startsWith('✅')?C.green:C.red, border:`1px solid ${feedback.startsWith('✅')?C.green:C.red}` }}>
          {feedback}
        </div>
      )}

      {/* Config panel */}
      {configMode && (
        <div style={{ background:C.card, borderRadius:8, padding:16, border:`1px solid ${C.border}` }}>
          <div style={{ color:C.gold, fontWeight:700, marginBottom:12, fontSize:14 }}>⚙️ Meta Configuration</div>
          <label style={{ display:'flex', alignItems:'center', gap:8, fontSize:13, color:C.muted, marginBottom:10, cursor:'pointer' }}>
            <input type="checkbox" checked={cfgForm.is_meta_verified} onChange={e=>setCfgForm(p=>({...p,is_meta_verified:e.target.checked}))} />
            Mark as Meta Verified
          </label>
          <input value={cfgForm.page_id} onChange={e=>setCfgForm(p=>({...p,page_id:e.target.value}))} placeholder="Facebook Page ID"
            style={{ width:'100%', background:'#0E1812', border:`1px solid ${C.border}`, borderRadius:6, color:C.text, padding:'8px 12px', fontSize:13, boxSizing:'border-box', marginBottom:10 }} />
          <button onClick={saveConfig} style={{ padding:'8px 16px', borderRadius:6, border:'none', background:C.gold, color:C.bg, fontWeight:700, cursor:'pointer', fontSize:13 }}>Save Config</button>
        </div>
      )}

      {/* Workflow — conditional on verified status */}
      {status?.is_meta_verified ? (
        <div style={{ background:C.card, borderRadius:8, padding:16, border:`1px solid ${C.border}` }}>
          <div style={{ color:C.green, fontWeight:700, marginBottom:12, fontSize:14 }}>🟢 Automated Distribution</div>
          <textarea value={message} onChange={e=>setMessage(e.target.value)} placeholder="Post caption…" rows={4}
            style={{ width:'100%', background:'#0E1812', border:`1px solid ${C.border}`, borderRadius:6, color:C.text, padding:'10px 12px', fontSize:13, resize:'vertical', boxSizing:'border-box', marginBottom:10 }} />
          <input value={imageUrl} onChange={e=>setImageUrl(e.target.value)} placeholder="Image URL (optional)"
            style={{ width:'100%', background:'#0E1812', border:`1px solid ${C.border}`, borderRadius:6, color:C.text, padding:'8px 12px', fontSize:13, boxSizing:'border-box', marginBottom:10 }} />
          {imageUrl && <img src={imageUrl} alt="preview" style={{ maxWidth:180, borderRadius:6, marginBottom:10, border:`1px solid ${C.border}` }} />}
          <button onClick={handlePost} disabled={posting||!message.trim()}
            style={{ padding:'10px 24px', borderRadius:6, border:'none', background:posting?C.dim:C.gold, color:C.bg, fontWeight:700, fontSize:14, cursor:posting?'not-allowed':'pointer' }}>
            {posting?'Posting…':'🚀 Post to Facebook & Instagram'}
          </button>
        </div>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
          <div style={{ padding:'12px 16px', borderRadius:8, border:`1px solid ${C.amber}`, background:'rgba(245,158,11,0.08)', color:C.amber, fontSize:13, lineHeight:1.6 }}>
            ⚠️ <strong>Meta Not Verified</strong> — Automated posting disabled. Use the manual workflow below.
          </div>

          {/* Compose + copy */}
          <div style={{ background:C.card, borderRadius:8, padding:16, border:`1px solid ${C.border}` }}>
            <div style={{ color:C.gold, fontWeight:700, marginBottom:10, fontSize:14 }}>📝 Compose & Copy Caption</div>
            <textarea value={message} onChange={e=>setMessage(e.target.value)} placeholder="Write caption…" rows={5}
              style={{ width:'100%', background:'#0E1812', border:`1px solid ${C.border}`, borderRadius:6, color:C.text, padding:'10px 12px', fontSize:13, resize:'vertical', boxSizing:'border-box', marginBottom:10 }} />
            <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
              <button onClick={()=>copyToClipboard(message,'cap')} disabled={!message.trim()}
                style={{ padding:'7px 16px', borderRadius:6, border:`1px solid ${C.gold}`, background:copied==='cap'?C.green:'transparent', color:copied==='cap'?'#fff':C.gold, cursor:'pointer', fontSize:12, fontWeight:600 }}>
                {copied==='cap'?'✅ Copied!':'📋 Copy Caption'}
              </button>
              {socialLinks.facebook && <a href={socialLinks.facebook} target="_blank" rel="noopener noreferrer" style={{ padding:'7px 14px', borderRadius:6, background:'#1877F2', color:'#fff', fontSize:12, fontWeight:700, textDecoration:'none' }}>Open Facebook ↗</a>}
              {socialLinks.instagram && <a href={socialLinks.instagram} target="_blank" rel="noopener noreferrer" style={{ padding:'7px 14px', borderRadius:6, background:'linear-gradient(135deg,#f09433,#dc2743,#bc1888)', color:'#fff', fontSize:12, fontWeight:700, textDecoration:'none' }}>Open Instagram ↗</a>}
              {socialLinks.twitterX && <a href={socialLinks.twitterX} target="_blank" rel="noopener noreferrer" style={{ padding:'7px 14px', borderRadius:6, background:'#000', color:'#fff', fontSize:12, fontWeight:700, textDecoration:'none' }}>Open X ↗</a>}
              {socialLinks.whatsapp && <a href={socialLinks.whatsapp} target="_blank" rel="noopener noreferrer" style={{ padding:'7px 14px', borderRadius:6, background:'#25D366', color:'#fff', fontSize:12, fontWeight:700, textDecoration:'none' }}>Open WhatsApp ↗</a>}
            </div>
          </div>

          {/* Asset library */}
          {festivalAssets.length > 0 && (
            <div style={{ background:C.card, borderRadius:8, padding:16, border:`1px solid ${C.border}` }}>
              <div style={{ color:C.gold, fontWeight:700, marginBottom:12, fontSize:14 }}>🖼️ Festival Asset Library</div>
              <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                {festivalAssets.map((a, i) => (
                  <div key={i} style={{ background:'#0E1812', borderRadius:6, padding:12, border:`1px solid ${C.border}` }}>
                    <div style={{ color:C.goldLt, fontWeight:600, fontSize:13, marginBottom:6 }}>{a.title}</div>
                    {a.imageUrl && (
                      <div style={{ display:'flex', gap:8, alignItems:'center', marginBottom:8 }}>
                        <img src={a.imageUrl} alt={a.title} style={{ width:72, height:72, objectFit:'cover', borderRadius:4, border:`1px solid ${C.border}` }} />
                        <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
                          <button onClick={()=>copyToClipboard(a.imageUrl!,`img-${i}`)}
                            style={{ padding:'4px 10px', borderRadius:6, border:`1px solid ${C.border}`, background:copied===`img-${i}`?C.green:'transparent', color:copied===`img-${i}`?'#fff':C.muted, cursor:'pointer', fontSize:11 }}>
                            {copied===`img-${i}`?'✅ URL Copied':'📋 Copy Image URL'}
                          </button>
                          <a href={a.imageUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize:11, color:C.gold, textDecoration:'none' }}>Preview ↗</a>
                        </div>
                      </div>
                    )}
                    <div style={{ color:C.muted, fontSize:12, lineHeight:1.5, marginBottom:8 }}>{a.text}</div>
                    <button onClick={()=>copyToClipboard(a.text,`txt-${i}`)}
                      style={{ padding:'5px 12px', borderRadius:6, border:`1px solid ${C.gold}`, background:'transparent', color:copied===`txt-${i}`?C.green:C.gold, cursor:'pointer', fontSize:12, fontWeight:600 }}>
                      {copied===`txt-${i}`?'✅ Copied!':'📋 Copy Text'}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {status?.last_post && (
        <div style={{ color:C.dim, fontSize:11, textAlign:'right' }}>Last post: {new Date(status.last_post).toLocaleString()}</div>
      )}
    </div>
  );
}
